import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { 
  Sparkles, Calendar as CalendarIcon, MapPin, AlertTriangle, 
  Check, TrendingUp, Clock, Eye, Globe
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useI18n } from '../context/I18nContext';

const AuroraPlanner = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const [auroraData, setAuroraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Fetch aurora forecast from NOAA
  useEffect(() => {
    const fetchAuroraForecast = async () => {
      setLoading(true);
      try {
        // NOAA Space Weather Prediction Center - 3-day forecast
        const response = await axios.get(
          'https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json',
          { timeout: 10000 }
        );
        
        if (response.data && response.data.length > 1) {
          // Parse Kp index data (skip header row)
          const forecasts = response.data.slice(1).map(row => ({
            time: row[0],
            kp: parseFloat(row[1]),
            observed: row[2] === 'observed'
          }));
          setAuroraData(forecasts);
        }
      } catch (error) {
        console.error('Failed to fetch aurora data:', error);
        // Use mock data as fallback
        setAuroraData(generateMockAuroraData());
      } finally {
        setLoading(false);
      }
    };

    fetchAuroraForecast();
    // Refresh every 30 minutes
    const interval = setInterval(fetchAuroraForecast, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Generate mock data for fallback
  const generateMockAuroraData = () => {
    const data = [];
    const now = new Date();
    for (let i = 0; i < 72; i += 3) {
      const time = new Date(now.getTime() + i * 60 * 60 * 1000);
      data.push({
        time: time.toISOString(),
        kp: Math.random() * 5 + (Math.random() > 0.8 ? 3 : 0), // Occasional spikes
        observed: i < 6
      });
    }
    return data;
  };

  // Calculate aurora visibility based on latitude and Kp index
  const calculateVisibility = (kp, lat) => {
    // Aurora oval expands with higher Kp
    // Approximate southern boundary of aurora visibility
    const auroraLatitudes = {
      9: 40, // Kp 9: visible down to 40°N
      8: 45,
      7: 50,
      6: 55,
      5: 60,
      4: 63,
      3: 65,
      2: 67,
      1: 68,
      0: 70
    };
    
    const absLat = Math.abs(lat);
    const requiredKp = Object.entries(auroraLatitudes)
      .find(([kpVal, latLimit]) => absLat >= latLimit)?.[0] || 9;
    
    const visibility = kp >= parseInt(requiredKp);
    const probability = visibility ? Math.min(100, (kp / parseInt(requiredKp)) * 80) :
                        Math.max(0, (kp / parseInt(requiredKp)) * 30);

    return {
      visible: visibility,
      probability: Math.round(probability),
      requiredKp: parseInt(requiredKp),
      message: visibility
        ? t('aurora.possibleAtLatitude')
        : `${t('aurora.needKp')} ${requiredKp}+ ${t('aurora.forVisibilityAt')} ${absLat.toFixed(1)}°`
    };
  };

  // Get current and max Kp
  const currentKp = auroraData?.[0]?.kp || 0;
  const maxKp = auroraData ? Math.max(...auroraData.map(d => d.kp)) : 0;
  const visibility = calculateVisibility(currentKp, latitude);
  const maxVisibility = calculateVisibility(maxKp, latitude);

  // Get Kp color
  const getKpColor = (kp) => {
    if (kp >= 7) return 'text-red-500';
    if (kp >= 5) return 'text-orange-500';
    if (kp >= 3) return 'text-amber-500';
    if (kp >= 1) return 'text-emerald-500';
    return 'text-slate-400';
  };

  const getKpBg = (kp) => {
    if (kp >= 7) return 'bg-red-500/20 border-red-500/30';
    if (kp >= 5) return 'bg-orange-500/20 border-orange-500/30';
    if (kp >= 3) return 'bg-amber-500/20 border-amber-500/30';
    if (kp >= 1) return 'bg-emerald-500/20 border-emerald-500/30';
    return 'bg-slate-500/20 border-slate-500/30';
  };

  const getKpLevel = (kp) => {
    if (kp >= 8) return t('aurora.extremeStorm');
    if (kp >= 7) return t('aurora.severeStorm');
    if (kp >= 6) return t('aurora.strongStorm');
    if (kp >= 5) return t('aurora.moderateStorm');
    if (kp >= 4) return t('aurora.minorStorm');
    if (kp >= 3) return t('aurora.active');
    if (kp >= 2) return t('aurora.unsettled');
    return t('aurora.quiet');
  };

  // Best viewing times (next 24h with highest Kp during night)
  const bestViewingTimes = useMemo(() => {
    if (!auroraData) return [];
    
    return auroraData
      .slice(0, 24)
      .filter(d => {
        const hour = new Date(d.time).getHours();
        return hour >= 21 || hour <= 4; // Night hours
      })
      .sort((a, b) => b.kp - a.kp)
      .slice(0, 3);
  }, [auroraData]);

  return (
    <div className="space-y-4">
      {/* Main Aurora Card */}
      <Card className="bg-gradient-to-br from-purple-500/20 via-green-500/10 to-cyan-500/20 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-400" />
            {t('aurora.title')}
            <Badge className="ml-2 bg-green-500/20 text-green-300 border-green-500/30">
              {t('aurora.liveForecast')}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Current Kp Index */}
              <div className={`rounded-lg p-4 border ${getKpBg(currentKp)} mb-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">{t('aurora.currentKpIndex')}</p>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-5xl font-mono font-bold ${getKpColor(currentKp)}`}>
                        {currentKp.toFixed(1)}
                      </span>
                      <span className="text-slate-400">/9</span>
                    </div>
                    <p className={`text-sm font-medium ${getKpColor(currentKp)}`}>
                      {getKpLevel(currentKp)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{t('aurora.yourVisibility')}</p>
                    <p className={`text-2xl font-bold ${visibility.visible ? 'text-green-400' : 'text-slate-500'}`}>
                      {visibility.probability}%
                    </p>
                    <p className="text-xs text-slate-500">{visibility.message}</p>
                  </div>
                </div>
              </div>

              {/* Kp Scale */}
              <div className="mb-4">
                <p className="text-xs text-slate-500 mb-2">{t('aurora.kpIndexScale')}</p>
                <div className="flex h-3 rounded-full overflow-hidden">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className={`flex-1 ${
                        i < 3 ? 'bg-emerald-500' :
                        i < 5 ? 'bg-amber-500' :
                        i < 7 ? 'bg-orange-500' : 'bg-red-500'
                      } ${currentKp >= i + 1 ? 'opacity-100' : 'opacity-30'}`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>{t('aurora.quiet')}</span>
                  <span>{t('aurora.active')}</span>
                  <span>{t('aurora.storm')}</span>
                </div>
              </div>

              {/* Location Info */}
              <div className="p-3 rounded-lg bg-slate-800/50 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-300">
                    {t('aurora.yourLocation')} {Math.abs(latitude).toFixed(2)}°{latitude >= 0 ? 'N' : 'S'}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {latitude >= 60
                    ? t('aurora.inAuroraZone')
                    : latitude >= 50
                    ? t('aurora.visibleStrongStorms')
                    : latitude >= 40
                    ? t('aurora.visibleExtremeStorms')
                    : t('aurora.tooFarSouth')
                  }
                </p>
              </div>

              {/* Best Viewing Times */}
              {bestViewingTimes.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('aurora.bestViewingTimes')}
                  </p>
                  <div className="space-y-2">
                    {bestViewingTimes.map((time, i) => (
                      <div 
                        key={i}
                        className="flex items-center justify-between p-2 rounded bg-slate-800/50"
                      >
                        <span className="text-sm text-white">
                          {format(new Date(time.time), 'EEE HH:mm')}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`font-mono ${getKpColor(time.kp)}`}>
                            Kp {time.kp.toFixed(1)}
                          </span>
                          {calculateVisibility(time.kp, latitude).visible && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Max Forecast */}
              <div className={`p-3 rounded-lg border ${getKpBg(maxKp)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">{t('aurora.peakForecast')}</span>
                  </div>
                  <div className="text-right">
                    <span className={`font-mono text-lg ${getKpColor(maxKp)}`}>
                      Kp {maxKp.toFixed(1)}
                    </span>
                    {maxVisibility.visible && (
                      <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30">
                        {t('aurora.visible')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 3-Day Forecast Chart */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            {t('aurora.kpForecast72h')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="w-full">
            <div className="flex gap-1 pb-2" style={{ minWidth: '600px' }}>
              {auroraData?.slice(0, 24).map((d, i) => {
                const hour = new Date(d.time).getHours();
                const isNight = hour >= 21 || hour <= 5;
                return (
                  <div key={i} className="flex flex-col items-center" style={{ width: '24px' }}>
                    <div 
                      className={`w-4 rounded-t ${getKpColor(d.kp).replace('text-', 'bg-')}`}
                      style={{ height: `${Math.max(4, d.kp * 8)}px` }}
                    />
                    <div className={`w-full h-1 ${isNight ? 'bg-indigo-500/50' : 'bg-amber-500/30'}`} />
                    <span className="text-[10px] text-slate-500 mt-1">
                      {hour}
                    </span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-indigo-500/50" />
              <span>{t('aurora.night')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1 bg-amber-500/30" />
              <span>{t('aurora.day')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-300">{t('aurora.photographyTips')}</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400 space-y-2">
          <p>📷 <strong>{t('aurora.settings')}</strong> {t('aurora.tipSettingsText')}</p>
          <p>🌙 <strong>{t('aurora.bestTime')}</strong> {t('aurora.tipBestTimeText')}</p>
          <p>🧭 <strong>{t('aurora.direction')}</strong> {t('aurora.tipDirectionText')}</p>
          <p>🏔️ <strong>{t('aurora.location')}</strong> {t('aurora.tipLocationText')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuroraPlanner;
